import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Shield, Users, Sparkles } from 'lucide-react';

export const AboutPraxisSection = () => {
  const values = [
    {
      icon: Heart,
      title: 'Einfühlsam',
      description: 'Jeder Patient wird bei mir mit Wärme und Verständnis behandelt.',
    },
    {
      icon: Shield,
      title: 'Professionell',
      description: 'Fundierte Therapiekonzepte auf aktuellem wissenschaftlichen Stand.',
    },
    {
      icon: Users,
      title: 'Individuell',
      description: 'Therapiepläne, die genau auf Ihre Bedürfnisse zugeschnitten sind.',
    },
    {
      icon: Sparkles,
      title: 'Persönlich',
      description: 'Eine familiäre Atmosphäre für eine vertrauensvolle Zusammenarbeit.',
    },
  ];

  return (
    <section id="praxis" className="section-padding bg-soft-gradient relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            Willkommen
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Über meine Praxis
          </h2>
          <p className="text-body font-body text-lg leading-relaxed">
            In meiner Praxis für Logopädie in Schöppingen steht der Mensch im Mittelpunkt. 
            Ich bieten Ihnen einen geschützten Raum, in dem Sie sich wohl und verstanden fühlen.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          {/* Left: Image/Visual */}
          <div className="relative">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
              {/* Placeholder visual with calming colors */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-light via-secondary-light to-accent-warm" />
              
              {/* Decorative pattern */}
              <div className="absolute inset-0 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id="praxis-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                      <circle cx="2" cy="2" r="1" fill="hsl(var(--primary))" />
                    </pattern>
                  </defs>
                  <rect x="0" y="0" width="100" height="100" fill="url(#praxis-pattern)" />
                </svg>
              </div>
              
              {/* Content overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-24 h-24 bg-card/90 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-elegant">
                    <Heart className="w-12 h-12 text-primary" />
                  </div>
                  <p className="text-heading font-heading text-2xl font-medium">Patientenorientiert</p>
                  <p className="text-body text-sm mt-2">seit der ersten Stunde</p>
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-card rounded-2xl shadow-card p-5 border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-heading text-sm font-semibold">Kassenzulassung</p>
                  <p className="text-caption text-xs">Alle Krankenkassen</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Text Content */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl lg:text-3xl font-medium text-heading">
              Ihre Gesundheit liegt mir am Herzen
            </h3>
            <div className="space-y-4 text-body font-body leading-relaxed">
              <p>
                Die Praxis JaNa Logo wurde mit dem Ziel gegründet, Menschen aller Altersgruppen 
                bei Sprach-, Sprech-, Stimm- und Schluckstörungen professionell zu unterstützen.
              </p>
              <p>
                Mein Ansatz ist es, jeden Patienten als Individuum zu betrachten und 
                gemeinsam die bestmögliche Therapie zu entwickeln. In einer ruhigen, 
                freundlichen Atmosphäre schaffen wir die Grundlage für erfolgreiche 
                Behandlungserfolge.
              </p>
              <p>
                Ich arbeit eng mit Ärzten, Kindergärten, Schulen und anderen 
                Therapeuten zusammen, um eine ganzheitliche Versorgung zu gewährleisten.
              </p>
            </div>
            
            {/* Highlight box */}
            <div className="bg-primary/5 border border-primary/10 rounded-2xl p-6 mt-8">
              <p className="text-body font-body italic">
&bdquo;Kommunikation ist der Schlüssel zur Welt – Ich helfe Ihnen, 
                diese Tür zu öffnen.&ldquo;
              </p>
              <p className="text-primary font-medium text-sm mt-3">— Praxisphilosophie</p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <Card 
              key={value.title}
              className="group bg-card hover:bg-card-elevated border-border hover:border-primary/20 transition-all duration-300 hover-lift"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-heading text-lg font-medium text-heading mb-2">
                  {value.title}
                </h4>
                <p className="text-body font-body text-sm">
                  {value.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutPraxisSection;
